import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchCustomerOrders } from '../lib/customerApi';
import './auth.css';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'processing', label: 'Processing' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancel', label: 'Cancelled' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const MyOrdersPage = () => {
  const { isAuthenticated, initializing, getToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const token = await getToken();
        const rows = await fetchCustomerOrders(token);
        if (!cancelled) setOrders(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load your orders.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, isAuthenticated]);

  const filteredOrders = useMemo(() => {
    if (activeTab === 'all') return orders;
    return orders.filter((order) => String(order.status || '').toLowerCase() === activeTab);
  }, [activeTab, orders]);

  if (initializing) {
    return <main className="orders-page"><p className="auth-subtitle">Loading your orders…</p></main>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/account?redirect=/my-orders" replace />;
  }

  return (
    <main className="orders-page">
      <header className="orders-header">
        <p className="auth-eyebrow">Order history</p>
        <h1 className="auth-title">My Orders</h1>
        <p className="auth-subtitle">Track and review your past and current orders.</p>
      </header>

      <div className="orders-card">
        <div className="profile-actions">
          <Link to="/profile" className="profile-link-btn">Profile</Link>
        </div>

        <div className="orders-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`orders-tab${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading && <p className="auth-subtitle">Loading orders…</p>}
        {error && <p className="auth-error" role="alert">{error}</p>}

        {!loading && !error && filteredOrders.length === 0 && (
          <p className="auth-subtitle">No orders in this category yet.</p>
        )}

        {!loading && filteredOrders.length > 0 && (
          <ul className="orders-list">
            {filteredOrders.map((order) => {
              const status = String(order.status || 'pending').toLowerCase();
              return (
                <li key={order.id} className="orders-item">
                  <div>
                    <p><strong>Order {order.id}</strong></p>
                    <p className="orders-item-meta">{formatDate(order.created_at || order.requested_date)}</p>
                    <p className="orders-item-meta">₦{Number(order.total || 0).toLocaleString()} · {order.payment_method || 'Payment pending'}</p>
                  </div>
                  <div>
                    <span className={`orders-status orders-status--${status}`}>{status}</span>
                    <p className="orders-item-meta" style={{ marginTop: '0.5rem' }}>
                      <Link to={`/order-status/${order.id}`}>View tracking</Link>
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
};

export default MyOrdersPage;
